import { useState, useRef, useEffect, useCallback } from "react";
import Konva from "konva";

export interface StageTransform {
  scale: number;
  x: number;
  y: number;
}

interface UseCanvasInteractionProps {
  image: HTMLImageElement | null;
  stageRef: React.RefObject<Konva.Stage | null>;
  stageTransform: StageTransform;
  setStageTransform: React.Dispatch<React.SetStateAction<StageTransform>>;
}

export const useCanvasInteraction = ({
  image,
  stageRef,
  stageTransform,
  setStageTransform,
}: UseCanvasInteractionProps) => {
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const lastPointerPosition = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space" && !e.repeat && image) {
        e.preventDefault();
        e.stopPropagation();
        setIsSpacePressed(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        e.preventDefault();
        setIsSpacePressed(false);
        setIsPanning(false);
        lastPointerPosition.current = null;
      }
    };
    window.addEventListener("keydown", handleKeyDown, { capture: true });
    window.addEventListener("keyup", handleKeyUp, { capture: true });
    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
      window.removeEventListener("keyup", handleKeyUp, { capture: true });
    };
  }, [image]);

  const handleMouseDown = useCallback(() => {
    if (isSpacePressed) {
      setIsPanning(true);
      const stage = stageRef.current;
      const pos = stage?.getPointerPosition();
      if (pos) {
        lastPointerPosition.current = pos;
      }
    }
  }, [isSpacePressed, stageRef]);

  const handleMouseMove = useCallback(() => {
    if (!isPanning || !lastPointerPosition.current) return;
    const stage = stageRef.current;
    const pos = stage?.getPointerPosition();
    if (pos) {
      const dx = pos.x - lastPointerPosition.current.x;
      const dy = pos.y - lastPointerPosition.current.y;
      setStageTransform((prev) => ({
        ...prev,
        x: prev.x + dx,
        y: prev.y + dy,
      }));
      lastPointerPosition.current = pos;
    }
  }, [isPanning, stageRef, setStageTransform]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    lastPointerPosition.current = null;
  }, []);

  const handleWheel = useCallback(
    (e: Konva.KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault();
      const scaleBy = 1.1;
      const stage = stageRef.current;
      const oldScale = stageTransform.scale;
      const pointerPosition = stage?.getPointerPosition();

      if (pointerPosition) {
        const mousePointTo = {
          x: (pointerPosition.x - stageTransform.x) / oldScale,
          y: (pointerPosition.y - stageTransform.y) / oldScale,
        };

        const newScale =
          e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

        setStageTransform({
          scale: newScale,
          x: pointerPosition.x - mousePointTo.x * newScale,
          y: pointerPosition.y - mousePointTo.y * newScale,
        });
      }
    },
    [stageRef, stageTransform, setStageTransform]
  );

  const getCursor = () => {
    if (isSpacePressed) {
      return isPanning ? "grabbing" : "grab";
    }
    return "crosshair";
  };

  return {
    isSpacePressed,
    isPanning,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    getCursor,
  };
};
